import React, { createContext, useContext, useState, useEffect } from 'react';
import { Resource } from '../types/resource';
import { getPdfUrl } from '../services/resourceService';
import toast from 'react-hot-toast';
import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';

interface ResourcesContextType {
  resources: Resource[];
  filteredResources: Resource[];
  loading: boolean;
  selectedType: string;
  setSelectedType: (type: string) => void;
  selectedLevel: string;
  setSelectedLevel: (level: string) => void;
  selectedTheme: string;
  setSelectedTheme: (theme: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  downloadResource: (resourceId: string) => Promise<void>;
  error: string | null;
}

const ResourcesContext = createContext<ResourcesContextType | undefined>(undefined);

export const useResources = () => {
  const context = useContext(ResourcesContext);
  if (context === undefined) {
    throw new Error('useResources must be used within a ResourcesProvider');
  }
  return context;
};

export const ResourcesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedTheme, setSelectedTheme] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchResources = async () => {
      try {
        setLoading(true);
        console.log("Fetching resources from Firestore...");
        
        // Récupérer toutes les ressources directement
        const snapshot = await getDocs(collection(db, 'resources'));
        
        console.log(`Found ${snapshot.docs.length} resources in Firestore`);
        
        const fetchedResources = snapshot.docs.map(doc => {
          const data = doc.data();
          console.log(`Resource ${doc.id}:`, data);
          
          // Convertir les timestamps Firestore en objets Date
          let createdAt = new Date();
          if (data.createdAt) {
            if (data.createdAt instanceof Timestamp) {
              createdAt = data.createdAt.toDate();
            } else if (data.createdAt.seconds) {
              // Si c'est un objet avec seconds et nanoseconds
              createdAt = new Date(data.createdAt.seconds * 1000);
            }
          }
          
          let updatedAt = new Date();
          if (data.updatedAt) {
            if (data.updatedAt instanceof Timestamp) {
              updatedAt = data.updatedAt.toDate();
            } else if (data.updatedAt.seconds) {
              updatedAt = new Date(data.updatedAt.seconds * 1000);
            }
          }
          
          // Assurer que tous les champs nécessaires sont présents
          return {
            id: doc.id,
            title: data.title || 'Sans titre',
            description: data.description || '',
            type: data.type || 'fiche',
            level: data.level || 'terminale',
            theme: data.theme || 'economie',
            chapter: data.chapter || '',
            pdfUrl: data.pdfUrl || '',
            downloads: data.downloads || 0,
            createdAt,
            updatedAt
          } as Resource;
        });
        
        // Log des ressources du thème "methode-bac"
        const methodeBacResources = fetchedResources.filter(r => r.theme === 'methode-bac');
        console.log(`Found ${methodeBacResources.length} Méthode Bac resources:`, methodeBacResources);
        
        setResources(fetchedResources);
      } catch (err) {
        console.error("Error fetching resources:", err);
        setError("Erreur lors du chargement des ressources");
      } finally {
        setLoading(false);
      }
    };

    fetchResources();
  }, []);

  const downloadResource = async (resourceId: string) => {
    try {
      const resource = resources.find(r => r.id === resourceId);
      if (!resource) {
        throw new Error('Ressource non trouvée');
      }
      
      console.log("Downloading resource:", resource);
      const url = await getPdfUrl(resource.pdfUrl);
      window.open(url, '_blank');
    } catch (error) {
      console.error("Error downloading resource:", error);
      toast.error('Erreur lors du téléchargement de la ressource');
    }
  };

  const filteredResources = resources.filter(resource => {
    // Filtrer par niveau
    if (selectedLevel !== 'all' && resource.level !== selectedLevel) return false;
    
    // Filtrer par thème
    if (selectedTheme !== 'all' && resource.theme !== selectedTheme) return false;
    
    // Filtrer par type
    if (selectedType !== 'all' && resource.type !== selectedType) return false;
    
    // Filtrer par recherche
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        resource.title.toLowerCase().includes(query) ||
        resource.description.toLowerCase().includes(query) ||
        (resource.chapter && resource.chapter.toLowerCase().includes(query))
      );
    }
    return true;
  });

  return (
    <ResourcesContext.Provider value={{
      resources,
      filteredResources,
      loading,
      selectedType,
      setSelectedType,
      selectedLevel,
      setSelectedLevel,
      selectedTheme,
      setSelectedTheme,
      searchQuery,
      setSearchQuery,
      downloadResource,
      error
    }}>
      {children}
    </ResourcesContext.Provider>
  );
};